import { useState } from "react";
import { zipUrl } from "../api";
import { formatJpy, formatJpyWithUsd } from "../money";
import type { FeedBatch, ImageItem } from "../types";
import { IconCopy, IconPlus, IconRefresh, IconStar, IconStop, IconTrash, IconZip } from "./icons";
import { ResultGrid } from "./ResultGrid";
import { Spinner, cx } from "./ui";

function when(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" });
  return sameDay ? time : `${d.toLocaleDateString("ja-JP", { month: "numeric", day: "numeric" })} ${time}`;
}

export function BatchCard({
  batch,
  variationLabel,
  modelLabels,
  onOpenImage,
  onToggleFavorite,
  onQuote,
  onDeleteImage,
  onRerun,
  onExtend,
  onRestore,
  onRetryFailed,
  onCancel,
  onDelete,
  onCopyPrompt,
  knownTags,
  onSaveTags,
}: {
  batch: FeedBatch;
  /** バリエーション戦略の表示名（same のときは undefined） */
  variationLabel?: string;
  modelLabels: Record<string, string>;
  onOpenImage: (image: ImageItem) => void;
  onToggleFavorite: (image: ImageItem) => void;
  onQuote: (image: ImageItem) => void;
  onDeleteImage: (image: ImageItem) => void;
  onRerun: () => void;
  onExtend: (n: number) => void;
  onRestore: () => void;
  onRetryFailed: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onCopyPrompt: () => void;
  knownTags?: string[];
  onSaveTags?: (image: ImageItem, tags: string[]) => void;
}) {
  const [promptOpen, setPromptOpen] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);

  const images = batch.images;
  const done = images.filter((i) => i.status === "done");
  const errors = images.filter((i) => i.status === "error").length;
  const favorites = done.filter((i) => i.favorite).length;
  const spent = images.reduce((s, i) => s + Number(i.cost_usd || 0), 0);
  const running = batch.status === "running";
  const progress = images.length ? ((done.length + errors) / images.length) * 100 : 0;

  const aspects = [...new Set(images.map((i) => i.aspect_ratio))];

  return (
    <section className="card overflow-hidden">
      {/* ---------------------------------------------------- カードヘッダ */}
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 pb-2.5 pt-3.5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {running && <Spinner className="h-4 w-4 shrink-0 text-brand-500" />}
            <h3 className="truncate text-[14.5px] font-bold text-slate-800">
              {batch.title || batch.prompt}
            </h3>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-slate-400">
            <span>{when(batch.created_at)}</span>
            <span>·</span>
            <span>
              {done.length}/{images.length}枚
            </span>
            {errors > 0 && <span className="font-semibold text-rose-500">失敗{errors}</span>}
            {spent > 0 && (
              <span className="tabular-nums" title={formatJpyWithUsd(spent)}>
                {formatJpy(spent)}
              </span>
            )}
            <span>·</span>
            <span>{aspects.join(" / ")}</span>
            <span>·</span>
            <span>品質{batch.config.quality}</span>
            {variationLabel && (
              <span className="rounded bg-brand-50 px-1.5 py-0.5 font-bold text-brand-700">{variationLabel}</span>
            )}
            {batch.status === "canceled" && <span className="font-semibold text-slate-500">停止済み</span>}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setPromptOpen((v) => !v)}
            className={cx("chip", promptOpen && "border-brand-400 bg-brand-50 text-brand-700")}
            title="プロンプトを表示"
          >
            プロンプト
          </button>
          <button type="button" onClick={onCopyPrompt} className="chip" title="プロンプト一式をコピー">
            <IconCopy className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={onRestore} className="chip" title="この設定をフォームに読み込む">
            設定を復元
          </button>
          {!running && (
            <button type="button" onClick={onRerun} className="chip" title="同じ設定でもう1バッチ生成">
              <IconRefresh className="h-3.5 w-3.5" />
              同じ設定で再生成
            </button>
          )}
          {!running && (
            <span className="relative">
              <button
                type="button"
                onClick={() => setExtendOpen((v) => !v)}
                className={cx("chip", extendOpen && "border-brand-400 bg-brand-50 text-brand-700")}
                title="このバッチに追加で生成"
              >
                <IconPlus className="h-3.5 w-3.5" />
                追加生成
              </button>
              {extendOpen && (
                <span className="absolute right-0 top-full z-10 mt-1 flex gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
                  {[2, 4, 10].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className="chip"
                      onClick={() => {
                        setExtendOpen(false);
                        onExtend(n);
                      }}
                    >
                      +{n}枚
                    </button>
                  ))}
                </span>
              )}
            </span>
          )}
          {!running && errors > 0 && (
            <button type="button" onClick={onRetryFailed} className="chip" title="失敗した枠だけ作り直す">
              <IconRefresh className="h-3.5 w-3.5" />
              失敗分
            </button>
          )}
          {favorites > 0 && (
            <a
              href={zipUrl(batch.id, true)}
              className="chip border-amber-300 bg-amber-50 text-amber-700"
              title="お気に入りだけZIP"
            >
              <IconStar className="h-3.5 w-3.5" filled />
              {favorites}
            </a>
          )}
          {done.length > 0 && (
            <a href={zipUrl(batch.id)} className="chip" title="全部ZIP">
              <IconZip className="h-3.5 w-3.5" />
              ZIP
            </a>
          )}
          {running ? (
            <button
              type="button"
              onClick={onCancel}
              className="chip border-rose-200 text-rose-600 hover:border-rose-300 hover:bg-rose-50"
              title="このバッチを停止"
            >
              <IconStop className="h-3.5 w-3.5" />
              停止
            </button>
          ) : (
            <button
              type="button"
              onClick={onDelete}
              className="chip text-slate-400 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600"
              title="バッチごと削除"
            >
              <IconTrash className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </header>

      {promptOpen && (
        <div className="mx-4 mb-2.5 space-y-1.5 rounded-xl bg-slate-50 p-3 text-[13px] leading-relaxed text-slate-600">
          <p className="whitespace-pre-wrap">{batch.prompt}</p>
          {batch.style && (
            <p className="whitespace-pre-wrap text-slate-500">
              <span className="font-semibold text-slate-400">スタイル：</span>
              {batch.style}
            </p>
          )}
          {batch.negative && (
            <p className="whitespace-pre-wrap text-slate-500">
              <span className="font-semibold text-slate-400">避ける：</span>
              {batch.negative}
            </p>
          )}
          {batch.sources?.length > 0 && (
            <div className="border-t border-slate-200 pt-1.5">
              <span className="font-semibold text-slate-400">参考にしたURL：</span>
              <ul className="mt-0.5 space-y-0.5">
                {batch.sources.map((s) => (
                  <li key={s.url} className="truncate text-[12.5px]">
                    <span className={s.ok ? "text-emerald-600" : "text-rose-500"}>
                      {s.ok ? "✓" : "×"}
                    </span>{" "}
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-slate-500 underline decoration-slate-300 hover:text-brand-600"
                    >
                      {s.title || s.url}
                    </a>
                    {!s.ok && s.error && <span className="ml-1 text-slate-400">（{s.error}）</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {running && (
        <div className="h-1 w-full bg-brand-100">
          <div className="h-full bg-brand-500 transition-[width] duration-300" style={{ width: `${progress}%` }} />
        </div>
      )}

      {/* ------------------------------------------------------- 画像本体 */}
      <div className="p-4 pt-3">
        <ResultGrid
          images={images}
          groupByModel={false}
          modelLabels={modelLabels}
          knownTags={knownTags}
          onOpen={onOpenImage}
          onToggleFavorite={onToggleFavorite}
          onQuote={onQuote}
          onDelete={onDeleteImage}
          onSaveTags={onSaveTags}
        />
      </div>
    </section>
  );
}
